import env from '@beam-australia/react-env';

import {
  COMMENTSTATUS_APPROVED,
  COMMENTSTATUS_AWAITING_APPROVAL,
  COMMENTSTATUS_NONE,
  COMMENTSTATUS_NOTAPPROVED,
  CONTENTTYPE_FILE,
  HEARINGSTATUS_ACTIVE,
  HEARINGSTATUS_AWAITING_CONCLUSION,
  HEARINGSTATUS_AWAITING_STARTDATE,
  HEARINGSTATUS_CONCLUDED,
  HEARINGSTATUS_CREATED,
  HEARINGSTATUS_DRAFT,
} from '../constants/api';
import { getAttr, getEntityId } from '../features/helpers/apiHelpers';
import { getFileExtensionFromFileName } from './files';
import {
  noSelectionKey,
  showAllKleHierarchies,
  showSelectedKleHierarchies,
  showUnselectedKleHierarchies,
} from './filterAndSortingsUtil';

export const optionsMapperName = (ele) => {
  return {
    key: getEntityId(ele),
    text: getAttr(ele, 'name'),
    content: getAttr(ele, 'text'),
  };
};

export const kleOptionsMapper = (ele) => ({
  key: getEntityId(ele),
  text: getAttr(ele, 'number') + ' - ' + getAttr(ele, 'name'),
});

export const attributeOptionsMapper = (attributeName) => (ele) => ({
  key: getEntityId(ele),
  text: getAttr(ele, attributeName),
});

export const optionsMapperIsActive = (translator) => (ele) => {
  var isActive = getAttr(ele, 'isActive');
  return {
    key: getEntityId(ele),
    text: getAttr(ele, 'name').concat(
      !isActive ? ' (' + translator('global.inactive') + ')' : '',
    ),
    disabled: !isActive,
  };
};

export const optionsMapperWithNoSelection = (translator) => (
  entities,
  attributeName = 'name',
) => {
  let options = [
    {
      key: noSelectionKey,
      text: translator('global.noneSelected'),
    },
  ];
  entities.forEach((entity) => {
    options.push({
      key: getEntityId(entity),
      text: getAttr(entity, attributeName),
    });
  });
  return options;
};

export const hearingStatusOptionsMapperWithNoSelection = (translator) => (
  hearingStatus,
) => {
  let options = [
    {
      key: noSelectionKey,
      text: translator('global.noneSelected'),
    },
  ];
  hearingStatus.forEach((entity) => {
    options.push({
      key: getEntityId(entity),
      text: translateHearingStatus(translator)(entity),
    });
  });
  return options;
};

export const kleOptionsMapperWithNumber = (translator) => (objects) => {
  let options = [
    {
      key: noSelectionKey,
      text: translator('global.noneSelected'),
    },
  ];
  objects.forEach((object) => {
    options.push({
      key: object.key,
      text: object.number,
    });
  });
  return options;
};

export const isChosenOptionsMapper = (translator) => {
  return [
    {
      key: showAllKleHierarchies,
      text: translator('pages.adminKleMappings.showAll'),
    },
    {
      key: showSelectedKleHierarchies,
      text: translator('pages.adminKleMappings.showSelected'),
    },
    {
      key: showUnselectedKleHierarchies,
      text: translator('pages.adminKleMappings.showUnselected'),
    },
  ];
};

export const translateHearingStatus = (translator) => (hearingStatus) => {
  const status = getAttr(hearingStatus, 'status');

  switch (status) {
    case HEARINGSTATUS_CREATED:
      return translator('hearingStatus.created');
    case HEARINGSTATUS_DRAFT:
      return translator('hearingStatus.draft');
    case HEARINGSTATUS_AWAITING_STARTDATE:
      return translator('hearingStatus.awaitingStartdate');
    case HEARINGSTATUS_ACTIVE:
      return translator('hearingStatus.active');
    case HEARINGSTATUS_AWAITING_CONCLUSION:
      return translator('hearingStatus.awaitingConclusion');
    case HEARINGSTATUS_CONCLUDED:
      return translator('hearingStatus.concluded');
    default:
      return '-';
  }
};

export const translateCommentStatus = (translator) => (commentStatus) => {
  const status = getAttr(commentStatus, 'status');

  switch (status) {
    case COMMENTSTATUS_NONE:
      return translator('commentStatus.none');
    case COMMENTSTATUS_AWAITING_APPROVAL:
      return translator('commentStatus.awaitingApproval');
    case COMMENTSTATUS_APPROVED:
      return translator('commentStatus.approved');
    case COMMENTSTATUS_NOTAPPROVED:
      return translator('commentStatus.notApproved');
    default:
      return '-';
  }
};
export const flattenMultipartFormDto = (
  obj,
  parent,
  excludedProperties,
  result = {},
) => {
  const keys = Object.keys(obj);
  const isArray = Array.isArray(obj);
  keys.forEach((key) => {
    let propName = '';
    if (parent) {
      propName = isArray
        ? parent + `[${key}]`
        : parent + `.${key.charAt(0).toUpperCase() + key.slice(1)}`;
    } else {
      propName = `${key.charAt(0).toUpperCase() + key.slice(1)}`;
    }

    if (
      typeof obj[key] === 'object' &&
      !excludedProperties.includes(key.toLowerCase())
    )
      flattenMultipartFormDto(obj[key], propName, excludedProperties, result);
    else {
      result[propName] = obj[key];
    }
  });
  return result;
};

export const mapFileContentDiff = (newContent) => {
  if (newContent.contentType === CONTENTTYPE_FILE) {
    if (!newContent.content.target) {
      return newContent;
    }
    const files = newContent.content.target.files;
    const fileDiffs = files.map((file) => ({
      isDeleteOperation: newContent.isDeleteOperation,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      lastModified: file.lastModified,
      fileContentUrl: !newContent.isDeleteOperation
        ? window.URL.createObjectURL(file)
        : window.URL.revokeObjectURL(newContent.content.fileContentUrl),
    }));
    newContent.content = fileDiffs;
  }
};

export const mapFileContentWithUrlToDownload = (baseApiUrl) => (
  fileContent,
) => {
  return {
    ...fileContent,
    attributes: {
      ...fileContent.attributes,
      urlToDownload: mapContentDownloadUrl(
        getEntityId(fileContent),
        baseApiUrl,
      ),
    },
  };
};

export const mapFileListItemToFile = (item) => {
  return {
    fileContentUrl: item.urlToDownload,
    target: {
      files: [
        {
          name: item.fileName,
        },
      ],
    },
  };
};

export const mapContentDownloadUrl = (contentId, baseApiUrl) => {
  const isDevelopment = env('EXT_TARGET_ENV') === 'development';
  const apiKey = env('EXT_X_API_HEADER');
  const query = isDevelopment && apiKey ? `?apiKey=${apiKey}` : '';

  return `${baseApiUrl}/content/${contentId}/download` + query;
};

export const mapFileContentToFileItems = (file) => {
  return {
    fileType: getFileExtensionFromFileName(getAttr(file, 'fileName')),
    fileName: getAttr(file, 'fileName'),
    urlToDownload: getAttr(file, 'urlToDownload'),
  };
};
